import { useEffect, useState } from "react";
import apiClient from "src/utils/apiClient";

interface HelpContent {
  content: string;
  version: string;
  lastModified: string;
  isOffline: boolean;
  error: boolean;
}

interface HelpContentResponse {
  content: string;
  version: string;
  lastModified: string;
}

const CACHE_KEY = "help_content_cache_v1";

// Format the backend ISO timestamp for display, e.g. "12 Aug 2026".
const formatLastModified = (iso: string): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

// Derive a human-friendly content version from the object's last-modified date.
const formatVersion = (iso: string): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `v${y}.${m}.${day}`;
};

/**
 * Fetches the help markdown + metadata from the backend.
 */
export const useHelpContent = (): HelpContent => {
  const [state, setState] = useState<HelpContent>({
    content: "",
    version: "",
    lastModified: "",
    isOffline: false,
    error: false,
  });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const res = await apiClient.get<HelpContentResponse>("/help/content");
        if (cancelled) return;
        const fresh = {
          content: res.data.content,
          version: formatVersion(res.data.lastModified),
          lastModified: formatLastModified(res.data.lastModified),
        };
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify(fresh));
        } catch {
          // ignore cache write errors
        }
        setState({ ...fresh, isOffline: false, error: false });
      } catch (err) {
        if (cancelled) return;
        // Fall back to the last successfully cached copy (offline support).
        try {
          const cached = localStorage.getItem(CACHE_KEY);
          if (cached) {
            const parsed = JSON.parse(cached) as Omit<HelpContent, "isOffline" | "error">;
            setState({ ...parsed, isOffline: true, error: false });
            return;
          }
        } catch {
          // ignore cache read/parse errors and fall through to the error state
        }
        setState((prev) => ({ ...prev, error: true }));
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
};
